import React, { useState, useEffect, useMemo } from 'react';
import { useFeathers } from '../../app/util';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { TypeDropdown } from '../TypeDropdown';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { stateCodeForName } from '../../app/util/clean-states';

interface SelectDistrictTypeProps {
  stateName?: string;
  stateCode?: string;
  value: string | null;
  onChange: (districtType: string | null, humanizedType?: string) => void;
  excludeTypes?: string[];
  restrictToTiers?: number[];
  /** When set, only these district types are shown (e.g. measure-supporting types) */
  allowTypes?: string[];
  placeholder?: string;
  /** Renders inside the select input (e.g. start adornment icon) */
  startAdornment?: React.ReactNode;
  /** MUI sx applied to the underlying select */
  sx?: object;
}

export const SelectDistrictType: React.FC<SelectDistrictTypeProps> = ({
  stateName,
  stateCode,
  value,
  onChange,
  excludeTypes = [],
  restrictToTiers,
  allowTypes,
  placeholder = 'Select district type',
}) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [stateConfiguration, setStateConfiguration] = useState<StateConfiguration | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (stateName || stateCode) {
      loadStateConfiguration();
    }
  }, [stateName, stateCode]);

  const loadStateConfiguration = async () => {
    if (!feathers) return;
    
    setLoading(true);
    
    try {
      let code = stateCode;
      if (!code && stateName) {
        code = stateCodeForName(stateName);
      }
      if (code) {
        const config = await feathers.getService('state-configurations').get(code.toLowerCase());
        setStateConfiguration(config);
      }
    } catch (error) {
      console.error('Error loading state configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const districtTypesSorted = useMemo(() => {
    if (!stateConfiguration?.typeConfigurations) return [];

    return stateConfiguration.typeConfigurations
      .sort((a: any, b: any) => {
        return a.tierNumber - b.tierNumber || a.type.localeCompare(b.type);
      })
      .filter((type: any) => type.tierNumber >= 1)
      .filter((type: any) => !excludeTypes.includes(type.type))
      .filter((type: any) => restrictToTiers === undefined || restrictToTiers.includes(type.tierNumber))
      .filter((type: any) => allowTypes === undefined || allowTypes.length === 0 || allowTypes.includes(type.type))
      .map((type: any) => ({
        type: type.type,
        title: humanizeDistrictType(type.type, stateConfiguration.typeConfigurations || [])
      }));
  }, [stateConfiguration, excludeTypes, restrictToTiers, allowTypes]);

  if (loading) {
    return (
      <Box>
        <Typography variant="body2">Loading...</Typography>
      </Box>
    );
  }

  if (districtTypesSorted.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="textSecondary">
          No available district types
        </Typography>
      </Box>
    );
  }

  const handleChange = (type: string) => {
    const selectedOption = districtTypesSorted.find(opt => opt.type === type);
    const humanizedType = selectedOption?.title;
    onChange(type, humanizedType);
  };

  return (
    <TypeDropdown
      placeholder={placeholder}
      value={value || null}
      options={districtTypesSorted}
      onChange={handleChange}
    />
  );
};
